import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = "";

/**
 * CEO Field Mode — Shannon's mobile battle station.
 *
 * Feb 14, 2026: "I really wanna be as hands-off as possible while I need
 * to be out in the field. When they say 'Can you come to my office?' I
 * need to be able to say 'Yes, later today, tomorrow, the next day.'"
 *
 * Design constraints (mobile-first, one-handed):
 *   - Single column, giant tap targets (min 56px)
 *   - No sidebar, no tabs to navigate — everything on one scroll
 *   - Top card: TODAY snapshot (hot count, meetings, alerts)
 *   - Middle: Signals — 🔥 Hot / 🌤 Warm / 👁 Watch — swipeable
 *   - Quick Answer: "Can I meet Wed 2pm?" typed in → yes/no
 *   - Bottom: last-scanned pill (autonomous — she never presses rescan)
 *
 * Access: Shannon-only (nivoomforward@gmail.com + slatricep@gmail.com).
 * Everyone else auto-redirects to /crew.
 */

const CEO_EMAILS = ["nivoomforward@gmail.com", "slatricep@gmail.com"];

// Editorial fonts injected once on mount
const injectFonts = () => {
  const ID = "mf-field-fonts";
  if (document.getElementById(ID)) return;
  const l = document.createElement("link");
  l.id = ID; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,600&family=Inter:wght@400;500;600;700;800&display=swap";
  document.head.appendChild(l);
};
const SERIF = "'Playfair Display', Georgia, serif";
const SANS = "'Inter', -apple-system, sans-serif";
const BRASS = "#B8915A";
const EMERALD = "#0d5c3a";
const BG = "#06111e";
const TEXT = "#F5EFE1";
const MUTED = "#9CA3AF";

const CEOField = () => {
  const [me, setMe] = useState(null);
  const [status, setStatus] = useState(null);
  const [today, setToday] = useState(null);
  const [signals, setSignals] = useState({ hot: [], warm: [], watch: [] });
  const [tab, setTab] = useState("hot");
  const [when, setWhen] = useState("");
  const [answer, setAnswer] = useState(null);
  const [rescanning, setRescanning] = useState(false);
  const [sitrep, setSitrep] = useState(null);
  const [testingSms, setTestingSms] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [s, t, sig, sr] = await Promise.all([
        fetch(`${BACKEND_URL}/api/ceo-field/status`, { credentials: "include" }).then((r) => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/ceo-field/today`, { credentials: "include" }).then((r) => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/ceo-field/signals?limit=25`, { credentials: "include" }).then((r) => r.ok ? r.json() : null),
        fetch(`${BACKEND_URL}/api/ceo-field/sitrep`, { credentials: "include" }).then((r) => r.ok ? r.json() : null),
      ]);
      setStatus(s); setToday(t);
      if (sig) setSignals({ hot: sig.hot || [], warm: sig.warm || [], watch: sig.watch || [] });
      if (sr) setSitrep(sr);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    injectFonts();
    // Auth guard — Shannon + Shaneka only
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((u) => {
        setMe(u);
        if (!u) {
          try { window.top.location.replace("/login"); } catch { window.location.replace("/login"); }
          return;
        }
        const email = (u.email || "").toLowerCase();
        const dashRole = u.dashboard_role || "";
        const isCeo = CEO_EMAILS.includes(email) || dashRole === "ceo" || dashRole === "coo";
        if (!isCeo) {
          try { window.top.location.replace("/crew"); } catch { window.location.replace("/crew"); }
          return;
        }
        loadAll();
      })
      .catch(() => {});
  }, [loadAll]);

  // Auto-refresh signals every 3 min so the page is always fresh
  useEffect(() => {
    if (!me) return;
    const id = setInterval(loadAll, 600 * 1000);
    return () => clearInterval(id);
  }, [me, loadAll]);

  const askQuickAnswer = async () => {
    if (!when) { setAnswer({ error: "Enter a date/time first" }); return; }
    try {
      const iso = new Date(when).toISOString();
      const r = await fetch(`${BACKEND_URL}/api/ceo-field/quick-answer?when=${encodeURIComponent(iso)}`, { credentials: "include" });
      const d = await r.json();
      setAnswer(d);
    } catch { setAnswer({ error: "Couldn't check calendar" }); }
  };

  const rescan = async () => {
    setRescanning(true);
    try {
      await fetch(`${BACKEND_URL}/api/ceo-field/rescan`, { method: "POST", credentials: "include" });
      await loadAll();
    } finally { setRescanning(false); }
  };

  const act = async (signal_id, action) => {
    const labels = {
      fire:    { verb: "Queueing outreach…", ok: "Signal queued for outreach",       fail: "Couldn't queue outreach" },
      dismiss: { verb: "Dismissing signal…",  ok: "Signal dismissed",                 fail: "Couldn't dismiss signal" },
      snooze:  { verb: "Snoozing signal…",    ok: "Signal snoozed",                    fail: "Couldn't snooze signal" },
    };
    const l = labels[action] || { verb: "Working…", ok: "Done", fail: "Failed" };
    const tid = toast.loading(l.verb);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ceo-field/act`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signal_id, action }),
      });
      toast.dismiss(tid);
      if (!r.ok) {
        let msg = `Server returned ${r.status}.`;
        try { const d = await r.json(); if (d.detail) msg = d.detail; } catch { /* keep default */ }
        toast.error(l.fail, { description: msg });
        return;
      }
      if (action === "fire") {
        toast.success(l.ok, {
          description: "Draft outreach will surface in Auto-Pilot for your one-tap approval.",
          duration: 6000,
        });
      } else {
        toast.success(l.ok);
      }
      await loadAll();
    } catch (e) {
      toast.dismiss(tid);
      toast.error(l.fail, { description: String(e.message || e) });
    }
  };

  const activeList = signals[tab] || [];

  if (!me) return (
    <div style={{ minHeight: "100vh", background: BG, color: TEXT, fontFamily: SANS, display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div>Loading Field Mode…</div>
    </div>
  );

  return (
    <div data-testid="ceo-field-root" style={{
      minHeight: "100vh",
      background: `radial-gradient(1200px 700px at 15% 5%, ${EMERALD}22 0%, transparent 62%), radial-gradient(900px 620px at 88% 90%, ${BRASS}18 0%, transparent 60%), linear-gradient(180deg, ${BG} 0%, #0a1929 100%)`,
      color: TEXT, fontFamily: SANS, paddingBottom: "3rem",
    }}>
      <style>{`
        @keyframes reveal { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .cf-card { animation: reveal 0.5s cubic-bezier(0.22,1,0.36,1) both; }
        .cf-tap  { min-height: 56px; touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
      `}</style>

      {/* Top bar */}
      <div style={{ padding: "1.2rem 1.2rem 0.8rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/dashboard" style={{ color: MUTED, textDecoration: "none", fontSize: "0.72rem", letterSpacing: "0.18em", textTransform: "uppercase" }} data-testid="cf-cockpit-link">
          ← Cockpit
        </Link>
        <div style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: "0.95rem", color: BRASS }}>Field Mode</div>
        <div style={{ width: 60 }} />
      </div>

      {/* TODAY card */}
      <div className="cf-card" style={{ padding: "0 1rem" }}>
        <div style={{
          background: "rgba(6,17,30,0.7)", backdropFilter: "blur(14px)",
          border: `1px solid ${BRASS}55`, borderRadius: 14,
          padding: "1.3rem 1.2rem", marginBottom: "1rem",
        }} data-testid="cf-today-card">
          <div style={{ color: BRASS, fontSize: "0.68rem", letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 800, marginBottom: 6 }}>
            {today?.date || "Today"}
          </div>
          <h1 style={{ fontFamily: SERIF, fontSize: "1.85rem", fontStyle: "italic", fontWeight: 700, margin: "0 0 1rem", lineHeight: 1.15 }}>
            Good morning, {me.given_name || me.name?.split(" ")[0] || "Shannon"}.
          </h1>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            <MetricTile label="🔥 Hot" value={today?.hot_signals ?? 0} accent="#ef4444" testid="cf-metric-hot" />
            <MetricTile label="📅 Today" value={today?.meetings_today ?? 0} accent={BRASS} testid="cf-metric-meetings" />
            <MetricTile label="🏗 Active" value={today?.projects_active ?? 0} accent="#00DC78" testid="cf-metric-projects" />
            <MetricTile label={today?.system_alert ? "🚨 Alert" : "✓ Clear"} value={today?.system_alert ? "!" : "OK"} accent={today?.system_alert ? "#ef4444" : "#00DC78"} testid="cf-metric-system" />
          </div>
        </div>
      </div>

      {/* Field Sitrep — 'Hey girl' Claude Sonnet briefing */}
      {sitrep?.briefing && (
        <div className="cf-card" style={{ padding: "0 1rem" }}>
          <div style={{
            background: `linear-gradient(160deg, ${BRASS}18, ${EMERALD}0f)`,
            border: `1px solid ${BRASS}55`, borderRadius: 14,
            padding: "1.2rem 1.2rem", marginBottom: "1rem",
            position: "relative", overflow: "hidden",
          }} data-testid="cf-sitrep-card">
            <div style={{ position: "absolute", top: 0, right: 0, width: 90, height: 90,
                          background: `radial-gradient(circle at top right, ${BRASS}44, transparent 65%)`, pointerEvents: "none" }} />
            <div style={{ color: BRASS, fontSize: "0.68rem", letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 800, marginBottom: 8 }}>
              Field Sitrep · {sitrep.cached ? "cached" : "fresh"}
            </div>
            <div style={{ fontFamily: SERIF, fontSize: "1.05rem", lineHeight: 1.55, color: TEXT, fontStyle: "italic" }} data-testid="cf-sitrep-text">
              {sitrep.briefing}
            </div>
            <button type="button" onClick={async () => {
              setTestingSms(true);
              try {
                const r = await fetch(`${BACKEND_URL}/api/ceo-field/sms-test`, { method: "POST", credentials: "include" });
                const d = await r.json();
                alert(d.sent ? "✓ Test SMS fired to your phone." : `SMS not sent: ${d.reason || "unknown"}`);
              } catch { alert("Couldn't send test SMS."); }
              finally { setTestingSms(false); }
            }} data-testid="cf-sms-test-btn"
              className="cf-tap"
              disabled={testingSms}
              style={{ marginTop: 12, background: "transparent", color: BRASS, border: `1px solid ${BRASS}66`,
                       borderRadius: 6, padding: "0.5rem 0.9rem", fontSize: "0.7rem",
                       fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
                       cursor: testingSms ? "wait" : "pointer" }}>
              {testingSms ? "Sending…" : "📱 Send test SMS to my phone"}
            </button>
          </div>
        </div>
      )}

      {/* Quick Answer — the killer field feature */}
      <div className="cf-card" style={{ padding: "0 1rem" }}>
        <div style={{
          background: `linear-gradient(160deg, ${EMERALD}18, ${BRASS}12)`,
          border: `1px solid ${BRASS}55`, borderRadius: 14,
          padding: "1.3rem 1.2rem", marginBottom: "1rem",
        }} data-testid="cf-quick-answer">
          <div style={{ color: BRASS, fontSize: "0.68rem", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 800, marginBottom: 8 }}>
            Quick Answer
          </div>
          <h2 style={{ fontFamily: SERIF, fontSize: "1.15rem", fontStyle: "italic", fontWeight: 600, margin: "0 0 0.9rem", color: TEXT }}>
            &ldquo;Can I come to your office…?&rdquo;
          </h2>
          <input
            type="datetime-local" value={when}
            onChange={(e) => setWhen(e.target.value)}
            data-testid="cf-quick-when"
            className="cf-tap"
            style={{
              width: "100%", background: "rgba(0,0,0,0.35)", color: TEXT,
              border: `1px solid ${BRASS}55`, borderRadius: 8,
              padding: "0.9rem 1rem", fontSize: "1rem", fontFamily: SANS,
              marginBottom: "0.8rem",
            }} />
          <button type="button" onClick={askQuickAnswer} data-testid="cf-quick-ask"
            className="cf-tap"
            style={{
              width: "100%", background: `linear-gradient(90deg, ${BRASS}, ${EMERALD})`,
              color: "#0a1929", border: "none", borderRadius: 8,
              padding: "0.95rem 1rem", fontSize: "0.95rem", fontWeight: 800,
              letterSpacing: "0.08em", textTransform: "uppercase",
              boxShadow: `0 12px 28px ${BRASS}44`, cursor: "pointer",
            }}>
            Ask the System
          </button>
          {answer && (
            <div data-testid="cf-quick-response" style={{
              marginTop: 12, padding: "0.9rem 1rem",
              background: answer.available ? "rgba(0,220,120,0.1)" : answer.error ? "rgba(239,68,68,0.1)" : `${BRASS}18`,
              border: `1px solid ${answer.available ? "rgba(0,220,120,0.5)" : answer.error ? "rgba(239,68,68,0.5)" : BRASS + "66"}`,
              borderRadius: 8, fontFamily: SERIF, fontStyle: "italic", fontSize: "1rem",
            }}>
              {answer.field_response || answer.error || "…"}
              {answer.suggested_alt && (
                <div style={{ marginTop: 6, fontFamily: SANS, fontSize: "0.8rem", fontStyle: "normal", color: MUTED }}>
                  {answer.reason}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Signal tabs */}
      <div style={{ padding: "0 1rem", display: "flex", gap: 6, marginBottom: "0.8rem" }} data-testid="cf-signal-tabs">
        <TabBtn active={tab === "hot"}   onClick={() => setTab("hot")}   label={`🔥 Hot (${signals.hot.length})`}   testid="cf-tab-hot" />
        <TabBtn active={tab === "warm"}  onClick={() => setTab("warm")}  label={`🌤 Warm (${signals.warm.length})`} testid="cf-tab-warm" />
        <TabBtn active={tab === "watch"} onClick={() => setTab("watch")} label={`👁 Watch (${signals.watch.length})`} testid="cf-tab-watch" />
      </div>

      {/* Signals list */}
      <div style={{ padding: "0 1rem" }} data-testid="cf-signals-list">
        {activeList.length === 0 && (
          <div style={{ padding: "2rem", textAlign: "center", color: MUTED, fontFamily: SERIF, fontStyle: "italic" }}>
            No {tab} signals right now. The sentinel scans every 6 hours — fresh intel soon.
          </div>
        )}
        {activeList.map((s, i) => (
          <SignalCard key={s.signal_id} sig={s} index={i} onAct={act} />
        ))}
      </div>

      {/* Autonomous footer — sentinel status */}
      <div style={{ padding: "1.5rem 1rem 0.5rem", textAlign: "center" }} data-testid="cf-sentinel-status">
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10,
                       background: "rgba(6,17,30,0.6)", border: `1px solid ${BRASS}33`,
                       borderRadius: 999, padding: "0.55rem 1.1rem",
                       fontSize: "0.72rem", color: MUTED,
                       letterSpacing: "0.16em", textTransform: "uppercase" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#00DC78", boxShadow: "0 0 8px #00DC78" }} />
          <span>Sentinel Active · {status?.minutes_since_scan != null ? `scanned ${status.minutes_since_scan}m ago` : "priming…"}</span>
          <button type="button" onClick={rescan} disabled={rescanning}
            style={{ marginLeft: 6, background: "transparent", color: BRASS, border: `1px solid ${BRASS}66`,
                     borderRadius: 4, padding: "0.25rem 0.55rem", fontSize: "0.62rem",
                     fontWeight: 800, cursor: rescanning ? "wait" : "pointer" }}
            data-testid="cf-rescan-btn">
            {rescanning ? "…" : "↻"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Reusable sub-components ────────────────────────────────────
const MetricTile = ({ label, value, accent, testid }) => (
  <div data-testid={testid} style={{
    background: "rgba(0,0,0,0.32)", border: `1px solid ${accent}44`,
    borderRadius: 8, padding: "0.6rem 0.4rem", textAlign: "center",
  }}>
    <div style={{ fontFamily: SERIF, fontSize: "1.4rem", fontWeight: 700, color: accent, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: "0.6rem", color: MUTED, letterSpacing: "0.06em", marginTop: 4, textTransform: "uppercase", fontWeight: 700 }}>{label}</div>
  </div>
);

const TabBtn = ({ active, onClick, label, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="cf-tap"
    style={{
      flex: 1, background: active ? `${BRASS}22` : "transparent",
      color: active ? TEXT : MUTED,
      border: `1px solid ${active ? BRASS + "88" : "rgba(255,255,255,0.08)"}`,
      borderRadius: 8, padding: "0.65rem 0.4rem",
      fontFamily: SANS, fontSize: "0.78rem", fontWeight: 700,
      cursor: "pointer", letterSpacing: "0.02em",
    }}>
    {label}
  </button>
);

const SignalCard = ({ sig, index, onAct }) => {
  const urgencyColor = sig.urgency === "hot" ? "#ef4444" : sig.urgency === "warm" ? BRASS : "#94a3b8";
  return (
    <div className="cf-card" data-testid={`cf-signal-${sig.signal_id}`}
      style={{
        background: "rgba(6,17,30,0.7)", border: `1px solid ${urgencyColor}44`,
        borderRadius: 12, padding: "1rem 1.1rem", marginBottom: "0.75rem",
        backdropFilter: "blur(10px)",
        animationDelay: `${Math.min(index, 8) * 60}ms`,
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
        <div style={{ color: urgencyColor, fontSize: "0.62rem", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 800 }}>
          {sig.source_label} · score {Math.round((sig.score || 0) * 100)}
        </div>
        <div style={{ fontSize: "0.62rem", color: MUTED, fontWeight: 700 }}>{sig.days_old ?? "?"}d old</div>
      </div>
      <div style={{ fontFamily: SERIF, fontSize: "1.02rem", fontWeight: 600, lineHeight: 1.35, marginBottom: 6, color: TEXT }}>
        {sig.title}
      </div>
      {sig.abstract && (
        <div style={{ fontSize: "0.82rem", color: MUTED, lineHeight: 1.55, marginBottom: 10 }}>
          {sig.abstract}
        </div>
      )}
      {sig.matched_keywords?.length > 0 && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
          {sig.matched_keywords.slice(0, 5).map((k) => (
            <span key={k} style={{
              background: `${urgencyColor}18`, color: urgencyColor,
              border: `1px solid ${urgencyColor}44`, borderRadius: 999,
              padding: "0.15rem 0.55rem", fontSize: "0.62rem", fontWeight: 700,
              letterSpacing: "0.04em",
            }}>{k}</span>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        {sig.url && (
          <a href={sig.url} target="_blank" rel="noreferrer"
            className="cf-tap"
            data-testid={`cf-signal-open-${sig.signal_id}`}
            style={{ flex: 1, background: "transparent", color: BRASS, border: `1px solid ${BRASS}66`,
                     borderRadius: 6, padding: "0.55rem", fontSize: "0.75rem", fontWeight: 700,
                     textAlign: "center", textDecoration: "none", letterSpacing: "0.06em" }}>
            Open source ↗
          </a>
        )}
        <button type="button" onClick={() => onAct(sig.signal_id, "fire")} data-testid={`cf-signal-fire-${sig.signal_id}`}
          className="cf-tap"
          style={{ flex: 1, background: `linear-gradient(90deg, ${BRASS}, ${EMERALD})`,
                   color: "#0a1929", border: "none", borderRadius: 6,
                   padding: "0.55rem", fontSize: "0.75rem", fontWeight: 800,
                   letterSpacing: "0.06em", cursor: "pointer" }}>
          🎯 Fire outreach
        </button>
        <button type="button" onClick={() => onAct(sig.signal_id, "dismiss")} data-testid={`cf-signal-dismiss-${sig.signal_id}`}
          className="cf-tap"
          style={{ background: "transparent", color: MUTED, border: "1px solid rgba(255,255,255,0.15)",
                   borderRadius: 6, padding: "0.55rem 0.7rem", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>
          ✕
        </button>
      </div>
    </div>
  );
};

export default CEOField;
